import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { getGatewayBaseUrl } from "../runtime/config";
import { PosIdentityGatewayClient } from "../runtime/gateway/identity-client";
import { posRuntimeDb } from "../runtime/local-store/pos-db";
import { readPosStorageDurability, type PosStorageDurability } from "../runtime/local-store/storage-durability";
import { readTerminalCredential } from "../runtime/local-store/terminal-state";

const DB_NAME = "more-pos-runtime-v1";
const DEDICATED_TERMINAL_DB_NAME = "more-pos-terminal-trust-v1";
const TERMINAL_KEY = "terminal:credential";
const DEDICATED_TERMINAL_KEY = "current";

type TerminalRecordState = "TRUSTED" | "PENDING" | "MISSING" | "UNREADABLE";

type RawProbe = {
  exists: boolean;
  version: number | null;
  stores: string[];
  legacyTerminalRecord: TerminalRecordState;
  dedicatedTerminalRecord: TerminalRecordState;
  journalCount: number | null;
  outboxCount: number | null;
};

type DiagnosticState = {
  dexie: "CHECKING" | "READY" | "ERROR";
  dexieError: string | null;
  raw: RawProbe | null;
  resolvedTerminal: "TRUSTED" | "PENDING" | "MISSING";
  localCredentialVersion: number | null;
  storageDurability: PosStorageDurability | "CHECKING";
  gatewayResolve: "SKIPPED" | "OK" | "ERROR";
  gatewayDetail: string | null;
};

function requestValue<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IDB_REQUEST_FAILED"));
  });
}

async function databaseExists(name: string) {
  const factory = indexedDB as IDBFactory & { databases?: () => Promise<Array<{ name?: string; version?: number }>> };
  if (!factory.databases) return true;
  const databases = await factory.databases();
  return databases.some((entry) => entry.name === name);
}

function terminalRecordState(value: unknown): TerminalRecordState {
  if (!value || typeof value !== "object") return "MISSING";
  const candidate = value as { deviceSecret?: unknown; identity?: unknown };
  if (typeof candidate.deviceSecret !== "string" || !candidate.deviceSecret) return "UNREADABLE";
  return candidate.identity && typeof candidate.identity === "object" ? "TRUSTED" : "PENDING";
}

async function probeDedicatedTerminalDatabase(): Promise<TerminalRecordState> {
  if (!(await databaseExists(DEDICATED_TERMINAL_DB_NAME))) return "MISSING";

  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DEDICATED_TERMINAL_DB_NAME);
    let createdByProbe = false;
    request.onupgradeneeded = () => {
      createdByProbe = true;
      request.transaction?.abort();
    };
    request.onsuccess = () => {
      if (createdByProbe) {
        request.result.close();
        reject(new Error("TERMINAL_TRUST_DB_DID_NOT_EXIST"));
        return;
      }
      resolve(request.result);
    };
    request.onerror = () => reject(request.error ?? new Error("TERMINAL_TRUST_DB_RAW_OPEN_FAILED"));
    request.onblocked = () => reject(new Error("TERMINAL_TRUST_DB_RAW_OPEN_BLOCKED"));
  });

  try {
    if (!db.objectStoreNames.contains("credentials")) return "UNREADABLE";
    const tx = db.transaction("credentials", "readonly");
    const record = await requestValue(tx.objectStore("credentials").get(DEDICATED_TERMINAL_KEY)) as { value?: unknown } | undefined;
    return terminalRecordState(record?.value);
  } catch {
    return "UNREADABLE";
  } finally {
    db.close();
  }
}

async function probeRawDatabase(): Promise<RawProbe> {
  const dedicatedTerminalRecord = await probeDedicatedTerminalDatabase();
  if (!(await databaseExists(DB_NAME))) {
    return {
      exists: false,
      version: null,
      stores: [],
      legacyTerminalRecord: "MISSING",
      dedicatedTerminalRecord,
      journalCount: null,
      outboxCount: null,
    };
  }

  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME);
    let createdByProbe = false;
    request.onupgradeneeded = () => {
      createdByProbe = true;
      request.transaction?.abort();
    };
    request.onsuccess = () => {
      if (createdByProbe) {
        request.result.close();
        reject(new Error("RUNTIME_DB_DID_NOT_EXIST"));
        return;
      }
      resolve(request.result);
    };
    request.onerror = () => reject(request.error ?? new Error("RUNTIME_DB_RAW_OPEN_FAILED"));
    request.onblocked = () => reject(new Error("RUNTIME_DB_RAW_OPEN_BLOCKED"));
  });

  try {
    const stores = Array.from(db.objectStoreNames);
    let legacyTerminalRecord: TerminalRecordState = "MISSING";
    let journalCount: number | null = null;
    let outboxCount: number | null = null;

    if (stores.includes("runtimeMeta")) {
      try {
        const tx = db.transaction("runtimeMeta", "readonly");
        const record = await requestValue(tx.objectStore("runtimeMeta").get(TERMINAL_KEY)) as { value?: unknown } | undefined;
        legacyTerminalRecord = terminalRecordState(record?.value);
      } catch {
        legacyTerminalRecord = "UNREADABLE";
      }
    }

    if (stores.includes("journal")) {
      try {
        const tx = db.transaction("journal", "readonly");
        journalCount = await requestValue(tx.objectStore("journal").count());
      } catch {
        journalCount = null;
      }
    }

    if (stores.includes("outbox")) {
      try {
        const tx = db.transaction("outbox", "readonly");
        outboxCount = await requestValue(tx.objectStore("outbox").count());
      } catch {
        outboxCount = null;
      }
    }

    return {
      exists: true,
      version: db.version,
      stores,
      legacyTerminalRecord,
      dedicatedTerminalRecord,
      journalCount,
      outboxCount,
    };
  } finally {
    db.close();
  }
}

function isActivationVisible() {
  return Array.from(document.querySelectorAll("button")).some((button) => button.textContent?.trim() === "Activate Terminal");
}

export function LocalStoreBootDiagnosticHost() {
  const [activationVisible, setActivationVisible] = useState(false);
  const [state, setState] = useState<DiagnosticState>({
    dexie: "CHECKING",
    dexieError: null,
    raw: null,
    resolvedTerminal: "MISSING",
    localCredentialVersion: null,
    storageDurability: "CHECKING",
    gatewayResolve: "SKIPPED",
    gatewayDetail: null,
  });

  useEffect(() => {
    const update = () => setActivationVisible(isActivationVisible());
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      let dexie: DiagnosticState["dexie"] = "READY";
      let dexieError: string | null = null;
      try {
        await posRuntimeDb.open();
      } catch (error) {
        dexie = "ERROR";
        dexieError = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
      }

      let raw: RawProbe | null = null;
      try {
        raw = await probeRawDatabase();
      } catch (error) {
        raw = {
          exists: true,
          version: null,
          stores: [],
          legacyTerminalRecord: "UNREADABLE",
          dedicatedTerminalRecord: "UNREADABLE",
          journalCount: null,
          outboxCount: null,
        };
        if (!dexieError) dexieError = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
      }

      let resolvedTerminal: DiagnosticState["resolvedTerminal"] = "MISSING";
      let localCredentialVersion: number | null = null;
      let gatewayResolve: DiagnosticState["gatewayResolve"] = "SKIPPED";
      let gatewayDetail: string | null = null;
      const storageDurability = await readPosStorageDurability();

      if (dexie === "READY") {
        try {
          const terminal = await readTerminalCredential();
          resolvedTerminal = terminal?.identity ? "TRUSTED" : terminal?.deviceSecret ? "PENDING" : "MISSING";
          localCredentialVersion = terminal?.identity?.credential_version ?? null;
          const gatewayBaseUrl = getGatewayBaseUrl();
          if (terminal?.deviceSecret && terminal.identity && navigator.onLine && gatewayBaseUrl) {
            const client = new PosIdentityGatewayClient({ baseUrl: gatewayBaseUrl });
            const result = await client.resolveTerminal(terminal.deviceSecret);
            if (result.ok && result.data) {
              gatewayResolve = "OK";
              gatewayDetail = `v${result.data.credential_version} ${result.data.credential_status}`;
            } else {
              gatewayResolve = "ERROR";
              gatewayDetail = result.error ?? "UNKNOWN_GATEWAY_ERROR";
            }
          }
        } catch (error) {
          gatewayResolve = "ERROR";
          gatewayDetail = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
        }
      }

      if (active) {
        setState({
          dexie,
          dexieError,
          raw,
          resolvedTerminal,
          localCredentialVersion,
          storageDurability,
          gatewayResolve,
          gatewayDetail,
        });
      }
    })();
    return () => { active = false; };
  }, []);

  if (!activationVisible) return null;

  const raw = state.raw;
  const dangerousMisclassification = state.dexie === "ERROR" || state.resolvedTerminal === "TRUSTED";
  const background = dangerousMisclassification ? "#fff1f0" : "#fff8dc";
  const border = dangerousMisclassification ? "#f3a6a0" : "#e9c75d";
  const title = state.dexie === "ERROR"
    ? "Local Runtime เปิดไม่สำเร็จ — ห้าม Activate ซ้ำ"
    : state.resolvedTerminal === "TRUSTED"
      ? "พบ Trusted Terminal ใน Local Store แต่หน้า Boot อ่านไม่ขึ้น"
      : state.resolvedTerminal === "PENDING"
        ? "พบ Device Secret แต่ Trusted Identity ไม่สมบูรณ์"
        : "ไม่พบ Trusted Terminal ใน Local Runtime ปัจจุบัน";

  return createPortal(
    <section style={{ position: "fixed", left: 24, right: 24, bottom: 24, zIndex: 100000, padding: "14px 18px", borderRadius: 14, border: `1px solid ${border}`, background, boxShadow: "0 14px 40px rgba(0,0,0,.18)", fontFamily: "system-ui, sans-serif", color: "#242424" }}>
      <strong style={{ display: "block", marginBottom: 6 }}>{title}</strong>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, lineHeight: 1.5 }}>
        <span>Dexie: <b>{state.dexie}</b></span>
        <span>DB: <b>{raw?.exists === false ? "MISSING" : raw?.version ?? "?"}</b></span>
        <span>Stores: <b>{raw?.stores.length ?? "?"}</b></span>
        <span>Resolved Terminal: <b>{state.resolvedTerminal}</b></span>
        <span>Dedicated Trust: <b>{raw?.dedicatedTerminalRecord ?? "CHECKING"}</b></span>
        <span>Legacy Trust: <b>{raw?.legacyTerminalRecord ?? "CHECKING"}</b></span>
        <span>Local Cred: <b>{state.localCredentialVersion ?? "?"}</b></span>
        <span>Storage: <b>{state.storageDurability}</b></span>
        <span>Gateway Resolve: <b>{state.gatewayResolve}</b>{state.gatewayDetail ? ` · ${state.gatewayDetail}` : ""}</span>
        <span>Journal: <b>{raw?.journalCount ?? "?"}</b></span>
        <span>Outbox: <b>{raw?.outboxCount ?? "?"}</b></span>
      </div>
      {state.dexieError ? <code style={{ display: "block", marginTop: 7, whiteSpace: "normal", overflowWrap: "anywhere", fontSize: 11 }}>{state.dexieError}</code> : null}
      <small style={{ display: "block", marginTop: 7 }}>ตัวตรวจนี้อ่านอย่างเดียว ไม่แสดง Device Secret ไม่ล้าง IndexedDB ไม่เปลี่ยน Terminal Credential และไม่แตะ Journal/Outbox</small>
    </section>,
    document.body,
  );
}
